// Importação de contatos por planilha no padrão Aeros Dark (mockup "Modal
// Importar A"): 3 etapas — arquivo, mapear colunas, resultado. Substitui a
// antiga tela /contacts/import. O lote vai num único POST
// /contacts/import-mapped e o progresso volta pelo socket (ImportacaoProgresso
// da tela de Contatos).
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { read, utils, writeFile } from "xlsx";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import LinearProgress from "@material-ui/core/LinearProgress";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import TableChartOutlinedIcon from "@material-ui/icons/TableChartOutlined";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import WarningAmberIcon from "@material-ui/icons/ReportProblemOutlined";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import {
  IMPORT_FIELDS,
  EXPORT_MODEL_ROW,
  guessMapping,
  buildImportBatch,
  mappingIssues,
  displayDateCell,
  extraField,
  isExtraField,
  EXTRA_PREFIX,
  importMode,
} from "../../pages/Contacts/contactRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  return {
    paper: { width: 600, maxWidth: "calc(100vw - 32px)", margin: 16, maxHeight: "calc(var(--vh-full) - 32px)" },
    inlinePaper: {
      width: 700,
      maxWidth: "100%",
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 16,
    },
    steps: { display: "flex", gap: 6, padding: "14px 24px 0" },
    step: { flex: 1, height: 3, borderRadius: 2, backgroundColor: isLight ? "rgba(15,20,40,0.1)" : "rgba(255,255,255,0.1)" },
    stepDone: { background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${AEROS.cyan})` },
    body: { padding: "16px 24px 20px", gap: 14 },
    dropzone: {
      border: `1px dashed ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.18)"}`,
      borderRadius: 14,
      padding: "36px 20px",
      textAlign: "center",
      cursor: "pointer",
      backgroundColor: theme.palette.aeros.surface,
      color: theme.palette.text.secondary,
      fontSize: 13,
      "& .MuiSvgIcon-root": { fontSize: 36, color: stateText(theme, AEROS.cyan, "info"), marginBottom: 8 },
      "& b": { color: theme.palette.text.primary, display: "block", fontSize: 13.5, marginBottom: 4 },
      "&:hover, &.active": { borderColor: AEROS.cyan, boxShadow: "0 0 0 3px rgba(34,211,238,0.12)" },
    },
    fileCard: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 12,
      padding: "10px 14px",
    },
    fileIcon: {
      width: 38,
      height: 38,
      borderRadius: 9,
      backgroundColor: alpha(AEROS.success, 0.12),
      color: AEROS.success,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 20 },
    },
    fileName: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    fileMeta: { fontSize: 11, color: theme.palette.text.secondary },
    table: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${border}`, borderRadius: 12, overflow: "hidden" },
    tableHead: {
      display: "grid",
      gridTemplateColumns: "1.2fr 1fr 1.3fr",
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      backgroundColor: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)",
      "& > div": { padding: "10px 14px" },
    },
    tableRow: {
      display: "grid",
      gridTemplateColumns: "1.2fr 1fr 1.3fr",
      alignItems: "center",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`,
      fontSize: 12.5,
      "&:last-child": { borderBottom: "none" },
    },
    colName: { padding: "9px 14px", fontWeight: 600, fontFamily: "ui-monospace, monospace", fontSize: 12, color: theme.palette.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    sample: { padding: "9px 14px", color: theme.palette.text.secondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    fieldCell: { padding: "6px 14px" },
    fieldSelect: {
      width: "100%",
      backgroundColor: isLight ? "#FFFFFF" : "#0E1119",
      border: `1px solid ${borderInput}`,
      borderRadius: 8,
      fontSize: 12,
      color: theme.palette.text.primary,
      "& .MuiSelect-root": { padding: "6px 28px 6px 10px", display: "flex", alignItems: "center", gap: 6 },
      "& .MuiSelect-icon": { color: theme.palette.aeros.textTertiary, right: 6, fontSize: 18 },
      "&:before, &:after": { display: "none" },
      "& .MuiSvgIcon-root.state": { fontSize: 14 },
    },
    fieldSelectWarn: { borderColor: alpha(AEROS.warning, 0.4) },
    fieldSelectSkip: { color: theme.palette.aeros.textTertiary },
    optionRow: { display: "flex", gap: 10, flexWrap: "wrap", "& > *": { flex: "1 1 160px" } },
    warn: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      color: stateText(theme, AEROS.warning, "warning"),
      backgroundColor: alpha(AEROS.warning, 0.08),
      border: `1px solid ${alpha(AEROS.warning, 0.25)}`,
      borderRadius: 10,
      padding: "9px 12px",
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    warnLink: { marginLeft: "auto", background: "none", border: "none", color: "inherit", cursor: "pointer", font: "inherit", fontWeight: 600 },
    warnList: {
      maxHeight: 120,
      overflowY: "auto",
      fontSize: 12,
      color: theme.palette.text.secondary,
      padding: "0 4px",
      ...theme.scrollbarStyles,
    },
    resultBox: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      padding: "24px 12px",
      gap: 8,
      "& .MuiSvgIcon-root": { fontSize: 44, color: AEROS.success },
    },
    resultTitle: { fontSize: 15, fontWeight: 700, color: theme.palette.text.primary, fontFamily: AEROS.fontDisplay },
    resultText: { fontSize: 13, color: theme.palette.text.secondary, lineHeight: 1.6, maxWidth: 380 },
    progressBox: { padding: "8px 0 4px", display: "flex", flexDirection: "column", gap: 8 },
    progressLine: { display: "flex", justifyContent: "space-between", fontSize: 13, color: theme.palette.text.primary, fontVariantNumeric: "tabular-nums" },
    progressBar: { height: 6, borderRadius: 3, backgroundColor: isLight ? "rgba(15,20,40,0.1)" : "rgba(255,255,255,0.1)", "& .MuiLinearProgress-bar": { borderRadius: 3, background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${AEROS.cyan})` } },
    counters: { fontSize: 12, color: theme.palette.text.secondary },
    resultError: { "& .MuiSvgIcon-root": { color: AEROS.error } },
  };
});

const STEP_LABELS = ["Escolher arquivo", "Mapear colunas", "Resultado"];
const SKIP = "";

// `inline` troca o Dialog por um cartão comum: é assim que a tela própria
// /contacts/import reaproveita o mesmo fluxo sem duplicar as 3 etapas.
const ContactImportModal = ({ open, onClose, onStarted, inline = false }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const aeros = useAerosSettingsStyles();
  const { user, socket } = useContext(AuthContext);

  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [validateContact, setValidateContact] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagId, setTagId] = useState("");
  const [showInvalid, setShowInvalid] = useState(false);
  const [sending, setSending] = useState(false);
  // { mode: "live"|"background", enviados, ignoradas } ao enviar; o progresso
  // e o resumo final chegam pelo socket enquanto o modal estiver aberto.
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [finished, setFinished] = useState(null); // { action, ...contadores }

  // Mesmo canal que a barra da tela de Contatos. Assinado enquanto o modal
  // estiver aberto — a importação de 100 linhas leva ~1 s, e sem isto a barra
  // da tela piscava atrás do backdrop e o usuário ficava sem saber o que houve.
  useEffect(() => {
    if (!open || !socket || !user?.companyId) return undefined;
    const canal = `company-${user.companyId}-importacao-contatos`;
    const onEvento = (data) => {
      if (data.action === "import-start") setProgress({ processados: 0, total: data.total });
      if (data.action === "import-progress") setProgress(data);
      if (["import-finish", "import-cancel", "import-error"].includes(data.action)) {
        setProgress(null);
        setFinished(data);
      }
    };
    socket.on(canal, onEvento);
    return () => socket.off(canal, onEvento);
  }, [open, socket, user?.companyId]);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setFile(null);
    setRows([]);
    setMapping({});
    setTagId("");
    setShowInvalid(false);
    setResult(null);
    setProgress(null);
    setFinished(null);
    api
      .get("/tags/list", { params: { kanban: 0 } })
      .then(({ data }) => setTags(Array.isArray(data) ? data : []))
      .catch(() => setTags([]));
  }, [open]);

  const columnCount = rows[0]?.length || 0;
  const dataRowCount = Math.max(0, rows.length - 1);
  const tagName = tags.find((t) => t.id === tagId)?.name;

  const batch = useMemo(
    () => buildImportBatch(rows, mapping, { extraTag: tagName }),
    [rows, mapping, tagName]
  );
  const missing = mappingIssues(mapping);

  const onDrop = (accepted) => {
    const f = accepted[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = read(e.target.result);
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = utils.sheet_to_json(ws, { header: 1, defval: "", raw: false });
        setRows(data);
        setMapping(guessMapping(data[0] || []));
        setFile(f);
        setStep(1);
      } catch (err) {
        toast.error(i18n.t("contactImport.invalidFile"));
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const { getRootProps, getInputProps, isDragActive, open: openPicker } = useDropzone({
    onDrop,
    multiple: false,
    noClick: step !== 0,
    accept: {
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "text/csv": [".csv"],
    },
  });

  const setField = (col, field) => {
    setMapping((prev) => {
      const next = { ...prev };
      // um campo só pode vir de uma coluna
      Object.keys(next).forEach((k) => {
        if (field !== SKIP && next[k] === field) delete next[k];
      });
      if (field === SKIP) delete next[col];
      else next[col] = field;
      return next;
    });
  };

  const sampleOf = (col) => {
    for (let i = 1; i < rows.length; i++) {
      const v = rows[i]?.[col];
      if (v !== "" && v !== null && v !== undefined) {
        return mapping[col] === "birthDate" ? displayDateCell(v) : String(v);
      }
    }
    return "—";
  };

  const downloadModel = () => {
    const wb = utils.book_new();
    utils.book_append_sheet(wb, utils.json_to_sheet([EXPORT_MODEL_ROW]), "Contatos");
    writeFile(wb, "modelo_contatos.xlsx");
  };

  const handleImport = async () => {
    if (missing.length || batch.contatos.length === 0) return;
    setSending(true);
    try {
      await api.post("/contacts/import-mapped", { contatos: batch.contatos, validateContact, updateExisting });
      setResult({ mode: importMode(batch.contatos.length), enviados: batch.contatos.length, ignoradas: batch.invalid.length });
      setStep(2);
      if (onStarted) onStarted();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const fieldLabel = (id) =>
    isExtraField(id)
      ? `${i18n.t("contactImport.extraField")}: ${id.slice(EXTRA_PREFIX.length)}`
      : IMPORT_FIELDS.find((f) => f.id === id)?.label || "";
  const StateIcon = ({ field }) =>
    field === SKIP ? (
      <RemoveCircleOutlineIcon className="state" style={{ color: "#5B6376" }} />
    ) : isExtraField(field) ? (
      <HelpOutlineIcon className="state" style={{ color: AEROS.warning }} />
    ) : (
      <CheckCircleIcon className="state" style={{ color: AEROS.success }} />
    );

  const conteudo = (
      <div className={m.form}>
        <div className={m.header}>
          <div>
            <div className={m.title}>{i18n.t("contactImport.title")}</div>
            <div className={m.subtitle}>
              {i18n.t("contactImport.step", { n: step + 1, total: 3 })} · {STEP_LABELS[step]}
            </div>
          </div>
          {!inline && (
            <IconButton className={m.closeButton} onClick={onClose} disabled={sending} aria-label="Fechar">
              <CloseIcon />
            </IconButton>
          )}
        </div>

        <div className={classes.steps}>
          {[0, 1, 2].map((i) => (
            <div key={i} className={`${classes.step} ${i <= step ? classes.stepDone : ""}`} />
          ))}
        </div>

        <div className={`${m.body} ${classes.body}`}>
          {step === 0 && (
            <div {...getRootProps({ className: `${classes.dropzone} ${isDragActive ? "active" : ""}` })}>
              <input {...getInputProps()} />
              <CloudUploadOutlinedIcon />
              <b>{i18n.t("contactImport.dropTitle")}</b>
              {i18n.t("contactImport.dropText")}
            </div>
          )}

          {step === 1 && (
            <>
              <div className={classes.fileCard}>
                <div className={classes.fileIcon}>
                  <TableChartOutlinedIcon />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={classes.fileName}>{file?.name}</div>
                  <div className={classes.fileMeta}>
                    {i18n.t("contactImport.fileMeta", { rows: dataRowCount, cols: columnCount })}
                  </div>
                </div>
                <input {...getInputProps()} />
                <button type="button" className={m.linkButton} style={{ marginLeft: 0 }} onClick={openPicker}>
                  {i18n.t("contactImport.changeFile")}
                </button>
              </div>

              <div className={classes.table}>
                <div className={classes.tableHead}>
                  <div>{i18n.t("contactImport.colSheet")}</div>
                  <div>{i18n.t("contactImport.colSample")}</div>
                  <div>{i18n.t("contactImport.colField")}</div>
                </div>
                {Array.from({ length: columnCount }, (_, col) => {
                  const field = mapping[col] || SKIP;
                  return (
                    <div className={classes.tableRow} key={col}>
                      <div className={classes.colName} title={String(rows[0][col] || "")}>
                        {String(rows[0][col] || utils.encode_col(col))}
                      </div>
                      <div className={classes.sample}>{sampleOf(col)}</div>
                      <div className={classes.fieldCell}>
                        <Select
                          value={field}
                          onChange={(e) => setField(col, e.target.value)}
                          className={`${classes.fieldSelect} ${field === SKIP ? classes.fieldSelectSkip : ""} ${isExtraField(field) ? classes.fieldSelectWarn : ""}`}
                          displayEmpty
                          renderValue={(v) => (
                            <>
                              <StateIcon field={v} />
                              {v === SKIP ? i18n.t("contactImport.skipColumn") : fieldLabel(v)}
                            </>
                          )}
                          MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
                        >
                          <MenuItem value={SKIP}>{i18n.t("contactImport.skipColumn")}</MenuItem>
                          {IMPORT_FIELDS.map((f) => (
                            <MenuItem key={f.id} value={f.id}>
                              {f.label}
                              {f.required ? " *" : ""}
                            </MenuItem>
                          ))}
                          {String(rows[0][col] || "").trim() && (
                            <MenuItem value={extraField(rows[0][col])}>
                              {i18n.t("contactImport.extraField")}: {String(rows[0][col]).trim()}
                            </MenuItem>
                          )}
                        </Select>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={classes.optionRow}>
                <div className={m.optionBox}>
                  <div style={{ flex: 1 }}>
                    <div className={m.optionTitle}>{i18n.t("contactImport.updateTitle")}</div>
                    <div className={m.optionDesc}>{i18n.t("contactImport.updateDesc")}</div>
                  </div>
                  <Switch
                    className={aeros.toggleSwitchSmall}
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    color="primary"
                  />
                </div>
                <div className={m.optionBox}>
                  <div style={{ flex: 1 }}>
                    <div className={m.optionTitle}>{i18n.t("contactImport.validateTitle")}</div>
                    <div className={m.optionDesc}>{i18n.t("contactImport.validateDesc")}</div>
                  </div>
                  <Switch
                    className={aeros.toggleSwitchSmall}
                    checked={validateContact}
                    onChange={(e) => setValidateContact(e.target.checked)}
                    color="primary"
                  />
                </div>
                <div className={m.optionBox}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={m.optionTitle}>{i18n.t("contactImport.applyTag")}</div>
                    <Select
                      value={tagId}
                      onChange={(e) => setTagId(e.target.value)}
                      displayEmpty
                      disableUnderline
                      style={{ fontSize: 11, fontWeight: 600, width: "100%", color: tagId ? "#C08BFF" : undefined }}
                      renderValue={(v) => (v ? tags.find((t) => t.id === v)?.name : i18n.t("contactImport.noTag"))}
                      MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
                    >
                      <MenuItem value="">{i18n.t("contactImport.noTag")}</MenuItem>
                      {tags.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>

              {missing.length > 0 && (
                <div className={classes.warn}>
                  <HelpOutlineIcon />
                  {i18n.t("contactImport.missing", { fields: missing.map(fieldLabel).join(", ") })}
                </div>
              )}
              {missing.length === 0 && batch.invalid.length > 0 && (
                <>
                  <div className={classes.warn}>
                    <WarningAmberIcon />
                    {i18n.t("contactImport.invalidLines", { count: batch.invalid.length })}
                    <button type="button" className={classes.warnLink} onClick={() => setShowInvalid((v) => !v)}>
                      {showInvalid ? i18n.t("contactImport.hideLines") : i18n.t("contactImport.showLines")}
                    </button>
                  </div>
                  {showInvalid && (
                    <div className={classes.warnList}>
                      {batch.invalid.map((l) => (
                        <div key={l.line}>
                          Linha {l.line}
                          {l.name ? ` · ${l.name}` : ""}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {step === 2 && result && (
            <div className={classes.resultBox}>
              {finished ? (
                <>
                  <CheckCircleIcon style={finished.action === "import-finish" ? undefined : { color: AEROS.warning }} />
                  <div className={classes.resultTitle}>
                    {i18n.t(
                      finished.action === "import-finish"
                        ? "contactImport.doneTitle"
                        : finished.action === "import-cancel"
                        ? "contactImport.cancelTitle"
                        : "contactImport.errorTitle"
                    )}
                  </div>
                  <div className={classes.resultText}>
                    {finished.action === "import-error"
                      ? finished.mensagem
                      : i18n.t("contactImport.doneText", {
                          criados: finished.criados || 0,
                          ignorados: finished.ignorados || 0,
                          invalidos: finished.invalidos || 0,
                          linhas: result.ignoradas,
                        })}
                  </div>
                </>
              ) : result.mode === "live" ? (
                <div className={classes.progressBox} style={{ width: "100%" }} aria-live="polite">
                  <div className={classes.progressLine}>
                    <span>{i18n.t("contactImport.progressTitle")}</span>
                    <span>
                      {progress ? `${progress.processados} / ${progress.total}` : `0 / ${result.enviados}`}
                    </span>
                  </div>
                  <LinearProgress
                    className={classes.progressBar}
                    variant={progress ? "determinate" : "indeterminate"}
                    value={progress && progress.total ? (progress.processados / progress.total) * 100 : 0}
                  />
                  <div className={classes.counters}>
                    {progress && progress.criados !== undefined
                      ? i18n.t("contactImport.counters", {
                          criados: progress.criados || 0,
                          ignorados: progress.ignorados || 0,
                          invalidos: progress.invalidos || 0,
                        })
                      : i18n.t("contactImport.progressHint")}
                  </div>
                </div>
              ) : (
                <>
                  <CheckCircleIcon />
                  <div className={classes.resultTitle}>{i18n.t("contactImport.startedTitle")}</div>
                  <div className={classes.resultText}>
                    {i18n.t("contactImport.startedText", { count: result.enviados, ignored: result.ignoradas })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className={m.footer}>
          {step < 2 && (
            <button type="button" className={m.linkButton} style={{ marginLeft: 0, color: "inherit", opacity: 0.8 }} onClick={downloadModel}>
              <GetAppIcon /> {i18n.t("contactImport.downloadModel")}
            </button>
          )}
          <div className={m.footerActions}>
            {step === 0 && (
              <Button variant="outlined" className={m.cancelButton} onClick={onClose}>
                {i18n.t("contactImport.cancel")}
              </Button>
            )}
            {step === 1 && (
              <>
                <Button variant="outlined" className={m.cancelButton} onClick={() => setStep(0)} disabled={sending}>
                  {i18n.t("contactImport.back")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className={m.submitButton}
                  onClick={handleImport}
                  disabled={sending || missing.length > 0 || batch.contatos.length === 0}
                >
                  {i18n.t("contactImport.importCount", { count: batch.contatos.length })}
                  {sending && <CircularProgress size={22} className={m.buttonProgress} />}
                </Button>
              </>
            )}
            {step === 2 && (
              <Button variant="contained" color="primary" className={m.submitButton} onClick={onClose}>
                {i18n.t("contactImport.close")}
              </Button>
            )}
          </div>
        </div>
      </div>
  );

  if (inline) return <div className={classes.inlinePaper}>{conteudo}</div>;

  return (
    <Dialog
      open={Boolean(open)}
      onClose={sending ? undefined : onClose}
      maxWidth={false}
      classes={{ paper: classes.paper }}
      scroll="paper"
    >
      {conteudo}
    </Dialog>
  );
};

export default ContactImportModal;
